import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  Tooltip,
  ReferenceDot,
} from "recharts";

/**
 * SparklineGrid — conditions_outlook evidence component.
 *
 * Five small sparklines (Comfort, Fire risk, Bugs, Site conditions, Stargazing)
 * each plotted across the dates in next_7_days. Each sparkline labels its peak
 * or trough value and the date it occurs.
 *
 * Polarity rule (spec §7): high comfort/site/stargazing is GOOD; high bug and
 * fire is BAD. Colour scales are inverted for bug and fire.
 *
 * Note: the rain key here is `precip_prob`, not `precip_probability_pct`.
 */

// ─── Series config ─────────────────────────────────────────────────────────────
// key = field in next_7_days[], label = display name, colour = line colour,
// goodHigh = true means higher is better (green at top), false means lower is better.
const SERIES = [
  { key: "comfort_index",    label: "Comfort",       goodHigh: true,  color: "#22C55E" },
  { key: "fire_index",       label: "Fire risk",     goodHigh: false, color: "#F97316" },
  { key: "bug_index",        label: "Bugs",          goodHigh: false, color: "#EAB308" },
  { key: "site_index",       label: "Site conditions", goodHigh: true, color: "#3B82F6" },
  { key: "stargazing_index", label: "Stargazing",     goodHigh: true, color: "#8B5CF6" },
];

// ─── Find peak or trough date ──────────────────────────────────────────────────
// For "good high" series, label the peak. For "bad high" series, label the trough.
const findExtremum = (days, key, goodHigh) => {
  if (!days || days.length === 0) return null;
  let bestIdx = 0;
  let bestVal = days[0][key];
  for (let i = 1; i < days.length; i++) {
    const v = days[i][key];
    if (goodHigh ? v > bestVal : v < bestVal) {
      bestVal = v;
      bestIdx = i;
    }
  }
  return { date: days[bestIdx].target_date, value: bestVal, idx: bestIdx };
};

// ─── Short date label ──────────────────────────────────────────────────────────
// "2026-08-12" → "Aug 12"
const shortDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// ─── Day-of-week label ─────────────────────────────────────────────────────────
// "2026-08-12" → "Tue"
const dayLabel = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { weekday: "short" });
};

// ─── Custom tooltip ────────────────────────────────────────────────────────────
const MiniTooltip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0];
  const seriesLabel = item.name || "";
  const rawDate = item.payload?.date || "";
  const dateStr = dayLabel(rawDate) ? `${dayLabel(rawDate)}, ${shortDate(rawDate)}` : shortDate(rawDate);
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 shadow text-[11px] leading-tight">
      {dateStr && (
        <p className="text-gray-500 dark:text-gray-400">{dateStr}</p>
      )}
      <p className="font-semibold text-gray-800 dark:text-gray-200">
        {seriesLabel}: {item.value}
      </p>
    </div>
  );
};

// ─── Single sparkline tile ─────────────────────────────────────────────────────
const SparklineTile = ({ series, days }) => {
  const extremum = findExtremum(days, series.key, series.goodHigh);
  const isPeak = series.goodHigh;
  const valueLabel = isPeak ? "Peak" : "Best";
  const dateLabel = extremum ? shortDate(extremum.date) : "";
  const weekdayLabel = extremum ? dayLabel(extremum.date) : "";

  // Build chart data from days array
  const chartData = days.map((d) => ({
    date: d.target_date,
    value: d[series.key],
  }));

  // Find the extremum index for the reference dot
  const extremumX = extremum ? days.findIndex((d) => d.target_date === extremum.date) : -1;

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
      {/* Header: label + extremum badge */}
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          {series.label}
        </span>
        {extremum && (
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            {valueLabel} {extremum.value} · {weekdayLabel} {dateLabel}
          </span>
        )}
      </div>

      {/* Sparkline */}
      <div className="h-16">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 6, bottom: 4, left: 4 }}>
            <Line
              type="monotone"
              dataKey="value"
              name={series.label}
              stroke={series.color}
              strokeWidth={2}
              dot={{ r: 2, fill: series.color }}
              activeDot={{ r: 4, fill: series.color, strokeWidth: 0 }}
            />
            {extremumX >= 0 && extremum && (
              <ReferenceDot
                x={extremum.date}
                y={extremum.value}
                r={4}
                fill={series.color}
                stroke="white"
                strokeWidth={1.5}
              />
            )}
            <Tooltip content={<MiniTooltip />} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Day labels along bottom */}
      <div className="flex justify-between mt-1">
        {days.map((d) => (
          <span key={d.target_date} className="text-[9px] text-gray-400 dark:text-gray-500">
            {dayLabel(d.target_date)}
          </span>
        ))}
      </div>
    </div>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
const SparklineGrid = ({ panel }) => {
  const f = panel.facts || {};
  const days = f.next_7_days || [];

  if (days.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Peak fire risk note */}
      {f.peak_fire_index != null && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Peak fire index this week: <span className="font-semibold text-orange-500">{f.peak_fire_index}</span>
        </p>
      )}

      {/* Five sparklines in a responsive grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {SERIES.map((s) => (
          <SparklineTile key={s.key} series={s} days={days} />
        ))}
      </div>
    </div>
  );
};

export default SparklineGrid;